import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Search, ShoppingCart, User, LogOut, Menu, X, ChevronDown, Wrench } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { api, productThumb } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import KoltLogo from "./KoltLogo";
import TicketsLauncher from "./TicketsLauncher";
import { LiveChatNavIcon } from "./LiveChatNavIcon";
import { SERVICE_CATEGORIES, useServiceCategories, categoryHref } from "../lib/categories";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "./ui/dropdown-menu";

const NAV_LINKS = [
  { label: "SERVICES",   to: "/shop",       hasMenu: true },
  { label: "INDUSTRIES", to: "/industries", hasMenu: false },
  { label: "ABOUT US",   to: "/about",      hasMenu: false },
  { label: "BLOG",       to: "/blog",       hasMenu: false },
  { label: "RESOURCES",  to: "/resources",  hasMenu: false, icon: Wrench },
  { label: "CONTACT",    to: "/contact",    hasMenu: false },
];

// Top capabilities strip — surfaces the live service categories as a quick-
// jump strip beneath the main nav. Each links to its own /shop/ route.
// The actual list is fetched from the backend via useServiceCategories();
// the static SERVICE_CATEGORIES export is used as a fallback only.

function ServicesMenu({ categories }) {
  return (
    <div
      className="absolute top-full left-1/2 -translate-x-1/2 mt-0 bg-white border border-slate-200 shadow-[0_8px_24px_-12px_rgba(4,30,66,0.18)] w-[520px] z-50"
      data-testid="services-mega-menu"
    >
      <div className="grid grid-cols-2 gap-0">
        {categories.map((c) => (
          <Link
            key={c.slug}
            to={categoryHref(c.slug)}
            className="group flex items-start gap-3 p-4 border-b border-r border-slate-100 hover:bg-kolt-paper-soft transition-colors"
            data-testid={`services-menu-${c.slug}`}
          >
            <div className="mt-0.5 w-8 h-8 flex items-center justify-center bg-kolt-slate group-hover:bg-kolt-orange transition-colors flex-shrink-0">
              <c.icon className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-display font-bold text-[13px] text-kolt-slate-dark group-hover:text-kolt-orange transition-colors">
                  {c.name}
                </span>
                {!c.available && (
                  <span className="text-[8px] font-mono tracking-widest font-bold text-kolt-orange">SOON</span>
                )}
              </div>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-400 mt-0.5 truncate">
                {c.spec}
              </div>
            </div>
          </Link>
        ))}
      </div>
      <Link
        to="/shop"
        className="block px-4 py-3 bg-kolt-slate-dark hover:bg-kolt-orange text-white text-center font-mono text-[11px] tracking-[0.3em] uppercase font-bold transition-colors"
        data-testid="services-menu-view-all"
      >
        View Full Catalog →
      </Link>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { categories: liveCategories } = useServiceCategories();
  const navCategories = liveCategories.length ? liveCategories : SERVICE_CATEGORIES;

  const submitSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term) {
      navigate(`/shop?search=${encodeURIComponent(term)}`);
      setSearchOpen(false);
      setSuggestions([]);
    }
  };

  // ── Live product suggestions (debounced typeahead) ──────────────────────
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestReq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (!searchOpen || term.length < 2) {
      setSuggestions([]);
      setSuggestLoading(false);
      return undefined;
    }
    setSuggestLoading(true);
    const seq = ++suggestReq.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { search: term } });
        // Ignore stale responses if the user kept typing.
        if (seq !== suggestReq.current) return;
        setSuggestions((data || []).slice(0, 6));
      } catch {
        if (seq === suggestReq.current) setSuggestions([]);
      } finally {
        if (seq === suggestReq.current) setSuggestLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, searchOpen]);

  const goToProduct = (p) => {
    setSearchOpen(false);
    setSuggestions([]);
    setQ("");
    navigate(`/shop/product/${p.slug}`);
  };

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-slate-200"
      data-testid="site-navbar"
    >
      <div className="container-page">
        <div className="flex items-center justify-between h-[88px] gap-6">
          <Link to="/" className="flex-shrink-0" data-testid="brand-logo">
            <KoltLogo className="h-12 w-auto" />
          </Link>
          <LiveChatNavIcon />
          <TicketsLauncher />

          {/* Center nav */}
          <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
            {NAV_LINKS.map((l) => (
              <div
                key={l.label}
                className="relative"
                onMouseEnter={() => l.hasMenu && setServicesOpen(true)}
                onMouseLeave={() => l.hasMenu && setServicesOpen(false)}
              >
                <Link
                  to={l.to}
                  className="group relative flex items-center gap-1 font-mono text-[12px] tracking-[0.28em] font-black text-kolt-slate-dark hover:text-kolt-orange transition-colors py-3"
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                >
                  {l.icon && <l.icon className="w-3.5 h-3.5 text-kolt-orange" strokeWidth={2.5} />}
                  {l.label}
                  {l.hasMenu && <ChevronDown className="w-3 h-3" strokeWidth={2.5} />}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-0 right-0 bottom-1 h-[2px] bg-kolt-orange origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"
                  />
                </Link>
                {l.hasMenu && servicesOpen && <ServicesMenu categories={navCategories} />}
              </div>
            ))}
          </nav>

          {/* Right utility */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSearchOpen((s) => !s)}
              className="p-2.5 hover:bg-slate-100 transition-colors"
              aria-label="Search"
              data-testid="nav-search-toggle"
            >
              <Search className="w-[18px] h-[18px] text-kolt-slate-dark" />
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-2.5 hover:bg-slate-100 transition-colors"
                    aria-label="Account"
                    data-testid="user-menu-btn"
                  >
                    <User className="w-[18px] h-[18px] text-kolt-slate-dark" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-none border border-slate-200">
                  <DropdownMenuLabel className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">
                    {user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/orders")} data-testid="menu-orders">Order History</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/quotes")} data-testid="menu-quotes">Saved Quotes</DropdownMenuItem>
                  {(user.role === "admin" || user.role === "accountant") && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Designer Console</DropdownMenuItem>
                    </>
                  )}
                  {(user.role === "accountant" || user.role === "admin") && (
                    <DropdownMenuItem onClick={() => navigate("/accounting")} data-testid="menu-accounting">Accounting Reports</DropdownMenuItem>
                  )}
                  {(user.role === "marketing" || user.role === "admin") && (
                    <DropdownMenuItem onClick={() => navigate("/marketing")} data-testid="menu-marketing">Marketing Command</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="p-2.5 hover:bg-slate-100 transition-colors"
                aria-label="Sign in"
                data-testid="nav-login-btn"
              >
                <User className="w-[18px] h-[18px] text-kolt-slate-dark" />
              </button>
            )}

            <Link
              to="/cart"
              className="relative p-2.5 hover:bg-slate-100 transition-colors"
              aria-label="Cart"
              data-testid="nav-cart-link"
            >
              <ShoppingCart className="w-[18px] h-[18px] text-kolt-slate-dark" />
              {count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-kolt-orange text-white text-[10px] font-black w-4 h-4 flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </Link>

            <button
              className="lg:hidden p-2.5 hover:bg-slate-100 transition-colors"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5 text-kolt-slate-dark" /> : <Menu className="w-5 h-5 text-kolt-slate-dark" />}
            </button>
          </div>
        </div>
      </div>

      {/* Secondary capabilities strip (matches reference) */}
      <nav
        className="hidden lg:block border-t border-slate-200 bg-white"
        data-testid="capabilities-strip"
      >
        <div className="container-page">
          <ul className="flex items-center justify-between gap-6 overflow-x-auto no-scrollbar">
            {navCategories.map((c) => (
              <li key={c.slug}>
                <Link
                  to={categoryHref(c.slug)}
                  className="group relative block py-3 font-display font-extrabold text-[15px] text-kolt-slate-dark hover:text-kolt-orange whitespace-nowrap transition-colors"
                  data-testid={`capabilities-strip-${c.slug}`}
                >
                  {c.name}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-0 right-0 bottom-1.5 h-[2px] bg-kolt-orange origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Expanding search bar */}
      {searchOpen && (
        <div className="border-t border-slate-200 bg-white" data-testid="search-dropdown">
          <div className="container-page py-4">
            <form onSubmit={submitSearch} className="flex gap-2 max-w-3xl mx-auto" data-testid="header-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search labels, materials, services..."
                  className="rounded-none border-slate-300 pl-11 h-12 font-body"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-kolt-orange hover:bg-kolt-orange-hover text-white rounded-none h-12 px-7 font-mono text-[11px] tracking-[0.22em] uppercase"
                data-testid="search-submit-btn"
              >
                Search
              </Button>
            </form>

            {/* Live product typeahead — appears as a panel under the input
                once the customer types 2+ characters. Click a row → product
                detail; hit Enter/Search → full results on /shop?search=… */}
            {q.trim().length >= 2 && (
              <div className="max-w-3xl mx-auto mt-3 border border-slate-200 bg-white shadow-sm" data-testid="search-suggestions-panel">
                {suggestLoading && suggestions.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-slate-500 font-mono uppercase tracking-[0.22em]" data-testid="search-suggestions-loading">Searching…</div>
                ) : suggestions.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-slate-500 font-mono uppercase tracking-[0.22em]" data-testid="search-suggestions-empty">No products match “{q.trim()}”. Press Enter to search anyway.</div>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="search-suggestions-list">
                    {suggestions.map((p) => (
                      <li key={p.product_id}>
                        <button
                          type="button"
                          onClick={() => goToProduct(p)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 group"
                          data-testid={`search-suggestion-${p.product_id}`}
                        >
                          <div className="w-12 h-12 bg-slate-100 flex-shrink-0 overflow-hidden">
                            {p.image_url && (
                              <img
                                src={productThumb(p.image_url, 120)}
                                alt={p.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-kolt-slate-dark truncate group-hover:text-kolt-orange transition-colors">{p.name}</div>
                            {p.short_description && (
                              <div className="text-xs text-slate-500 truncate">{p.short_description}</div>
                            )}
                          </div>
                          {p.starting_price != null && (
                            <div className="text-[11px] font-mono tracking-widest text-slate-500 flex-shrink-0">
                              from <span className="text-kolt-slate-dark font-bold">${Number(p.starting_price).toFixed(2)}</span>
                            </div>
                          )}
                        </button>
                      </li>
                    ))}
                    <li>
                      <button
                        type="button"
                        onClick={submitSearch}
                        className="w-full text-left px-3 py-2.5 text-[11px] font-mono uppercase tracking-[0.22em] text-kolt-orange hover:bg-slate-50"
                        data-testid="search-view-all-btn"
                      >
                        View all results for “{q.trim()}” →
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-88px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-page py-4">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase font-bold text-kolt-orange py-2 border-b border-slate-100">
              Capabilities
            </div>
            {navCategories.map((c) => (
              <Link
                key={c.slug}
                to={categoryHref(c.slug)}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between py-3 font-display font-extrabold text-sm text-kolt-slate-dark hover:text-kolt-orange border-b border-slate-100"
              >
                {c.name}
                {!c.available && (
                  <span className="font-mono text-[9px] tracking-widest font-bold text-kolt-orange">SOON</span>
                )}
              </Link>
            ))}
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase font-bold text-kolt-orange py-3 mt-3 border-b border-slate-100">
              Company
            </div>
            {NAV_LINKS.filter((l) => !l.hasMenu).map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="block py-3 font-mono text-[11px] tracking-[0.28em] font-semibold text-kolt-slate-dark hover:text-kolt-orange border-b border-slate-100"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/quote"
              onClick={() => setMobileOpen(false)}
              className="block py-4 font-mono text-[11px] tracking-[0.28em] font-bold text-white bg-kolt-orange text-center mt-4"
            >
              REQUEST A QUOTE →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
